/**
 * bookingStatus — single source of truth for translating raw backend
 * booking statuses into the customer-facing 7-stage TREL workflow:
 *
 *   Requested → Concierge Responses → Concierge Selected →
 *   Payment Completed → Planning → Confirmed → Completed
 *
 * Backend reality (kept stable for API compatibility):
 *   Pending Acceptance → Quote Sent → Accepted → Confirmed → Completed
 *   plus the parallel `payment_status` (pending | awaiting_customer | paid).
 *
 * This module maps a `{status, payment_status}` pair to the canonical
 * step + label. Stage 1 + 2 are owned by the brief flow (no booking yet);
 * once a booking exists, we're always at stage ≥ 3.
 */

export const STAGES = [
  { key: "requested",          label: "Requested" },
  { key: "responses",          label: "Concierge Responses" },
  { key: "selected",           label: "Concierge Selected" },
  { key: "paid",               label: "Payment Completed" },
  { key: "planning",           label: "Planning" },
  { key: "confirmed",          label: "Confirmed" },
  { key: "completed",          label: "Completed" },
];

/**
 * Resolve a canonical stage from a booking record. Defensive against
 * missing fields — defaults to "selected" once a booking exists.
 */
export function stageOf(booking) {
  if (!booking) return null;
  const s = String(booking.status || "").toLowerCase();
  const ps = String(booking.payment_status || "").toLowerCase();
  if (["canceled", "cancelled"].includes(s)) return "canceled";
  if (s === "completed") return "completed";
  if (s === "confirmed" && ps === "paid") {
    // After payment, the concierge enters Planning. The natural progression
    // is Planning → Confirmed (final reconfirmation by concierge) →
    // Completed. We use the booking's `confirmed_at` timestamp (set when
    // the concierge marks the plan locked in) to advance past Planning.
    return booking.plan_locked_at ? "confirmed" : "planning";
  }
  if (s === "confirmed") return "confirmed";
  if (s === "accepted") return "planning";
  if (s === "quote sent") return "selected";
  if (s === "pending acceptance") return "selected";
  return "selected";
}

/** Index of the current stage in STAGES[] — useful for pipeline UIs. */
export function stageIndex(stage) {
  const i = STAGES.findIndex((s) => s.key === stage);
  return i === -1 ? -1 : i;
}

/** Human-friendly label for the current stage (e.g. "Payment Completed"). */
export function stageLabel(booking) {
  const k = stageOf(booking);
  if (k === "canceled") return "Canceled";
  const s = STAGES.find((x) => x.key === k);
  return s?.label || "Selected";
}

/**
 * Whether the customer still owes the concierge service fee. Used to
 * surface a "Pay concierge fee" CTA on the dashboard or booking detail.
 *
 * Two ready-to-pay flows are recognised:
 *   1. Legacy direct booking — status = "Accepted", payment_status = "pending"
 *      (concierge accepted; customer just needs to charge the fee).
 *   2. Brief marketplace — status = "Quote Sent",
 *      payment_status = "awaiting_customer" (concierge sent a custom quote
 *      after the customer selected them).
 * In both cases the concierge has explicitly acted, so we can confidently
 * prompt for payment. We still hide the pill while `payment_status === "paid"`
 * to guarantee idempotency.
 */
export function awaitingPayment(booking) {
  if (!booking) return false;
  const s = String(booking.status || "").toLowerCase();
  const ps = String(booking.payment_status || "").toLowerCase();
  if (ps === "paid") return false;
  return s === "accepted" || s === "quote sent";
}
